import { useEffect, useState } from "react";

const ConfirmModal = ({
    open,
    type = "warning", // 'warning', 'danger', 'info', 'success'
    title = "Confirm Action",
    children, // React children for custom content
    buttons = "ok-cancel", // Preset: 'ok', 'ok-cancel', 'yes', 'yes-no', 'cancel'
    customButtons, // Array of { label, action: 'confirm'|'cancel'|'custom', key?, onClick? }
    onConfirm,
    onCancel,
    onClose,
    showCloseButton = true,
    loading = false,
    // Legacy props support (deprecated)
    content,
    message,
    confirmText,
    cancelText,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const finalContent =
        children || content || message || "Are you sure you want to proceed?";
    const finalConfirmText = confirmText || "OK";
    const finalCancelText = cancelText || "Cancel";
    const isLoading = loading || isSubmitting;

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && !isSubmitting) {
                handleCancel();
            }
            if (e.key === "Enter" && !isSubmitting) {
                handleConfirm();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, onConfirm, isSubmitting]);

    const getColorClass = () => {
        switch (type) {
            case "danger":
                return "bg-danger";
            case "info":
                return "bg-info";
            case "success":
                return "bg-success";
            case "warning":
            default:
                return "bg-warning";
        }
    };

    const getTextColor = () => {
        switch (type) {
            case "danger":
            case "info":
            case "success":
                return "text-white";
            case "warning":
            default:
                return "text-dark";
        }
    };

    const getButtonClass = () => {
        switch (type) {
            case "danger":
                return "btn-danger";
            case "info":
                return "btn-info";
            case "success":
                return "btn-success";
            case "warning":
            default:
                return "btn-warning";
        }
    };

    const getCloseButtonClass = () => {
        switch (type) {
            case "warning":
                return "btn-close";
            default:
                return "btn-close btn-close-white";
        }
    };

    const handleConfirm = async () => {
        if (isLoading) return;
        try {
            setIsSubmitting(true);
            const maybePromise = onConfirm?.();
            if (maybePromise && typeof maybePromise.then === "function") {
                await maybePromise;
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (isLoading) return;
        onCancel?.();
        onClose?.();
    };

    // Default button presets
    const getDefaultButtons = () => {
        const presets = {
            ok: [{ label: "OK", action: "confirm" }],
            "ok-cancel": [
                { label: finalCancelText, action: "cancel" },
                { label: finalConfirmText, action: "confirm" },
            ],
            yes: [{ label: "Yes", action: "confirm" }],
            "yes-no": [
                { label: "No", action: "cancel" },
                { label: "Yes", action: "confirm" },
            ],
            cancel: [{ label: "Cancel", action: "cancel" }],
        };
        return presets[buttons] || presets["ok-cancel"];
    };

    const finalButtons = customButtons || getDefaultButtons();

    if (!open) {
        return null;
    }

    return (
        <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isLoading) handleCancel();
            }}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className={`modal-header ${getColorClass()}`}>
                        <h5 className={`modal-title ${getTextColor()} fw-bold`}>
                            {title}
                        </h5>
                        {showCloseButton && (
                            <button
                                type="button"
                                className={getCloseButtonClass()}
                                onClick={() => !isLoading && handleCancel()}
                                disabled={isLoading}
                                aria-label="Close"
                            ></button>
                        )}
                    </div>

                    <div className="modal-body">
                        {typeof finalContent === "string" ? (
                            <div
                                className="text-dark"
                                style={{ whiteSpace: "pre-wrap" }}
                            >
                                {finalContent}
                            </div>
                        ) : (
                            finalContent
                        )}
                    </div>

                    <div className="modal-footer">
                        {finalButtons.map((btn, idx) => (
                            <button
                                key={btn.key || idx}
                                type="button"
                                className={`btn ${
                                    btn.action === "confirm"
                                        ? getButtonClass()
                                        : "btn-secondary"
                                }`}
                                onClick={() => {
                                    if (isLoading) return;
                                    if (btn.action === "confirm") {
                                        handleConfirm();
                                    } else if (btn.action === "cancel") {
                                        handleCancel();
                                    } else if (btn.onClick) {
                                        btn.onClick();
                                    }
                                }}
                                disabled={isLoading}
                            >
                                {isLoading && btn.action === "confirm" && (
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                )}
                                {btn.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
